/**
 * Parse salary string to extract min and max values
 * @param salaryString - Salary string in format "₹6,00,000 - ₹10,00,000"
 * @returns Object with min and max numeric values, or null if parsing fails
 */
export const parseSalaryRange = (salaryString?: string): { min: number; max: number } | null => {
  if (!salaryString || typeof salaryString !== 'string') {
    return null;
  }

  try {
    // Match pattern: ₹6,00,000 - ₹10,00,000
    // Remove ₹ symbol and spaces, then split by " - "
    const cleaned = salaryString.replace(/₹/g, '').trim();
    const parts = cleaned.split(/\s*-\s*/);

    if (parts.length === 2) {
      // Remove commas and convert to numbers
      const min = parseInt(parts[0].replace(/,/g, ''), 10);
      const max = parseInt(parts[1].replace(/,/g, ''), 10);

      if (!isNaN(min) && !isNaN(max)) {
        return { min, max };
      }
    }
  } catch (error) {
    console.error('Error parsing salary range:', error);
  }

  return null;
};

/**
 * Format min and max salary values into salary_range string
 * @param minSalary - Minimum salary value
 * @param maxSalary - Maximum salary value
 * @returns Formatted salary range string (e.g., "15-18k" or "₹15,000 - ₹18,000")
 */
export const formatSalaryRange = (minSalary: number | string, maxSalary: number | string): string | null => {
  const min = typeof minSalary === 'string' ? parseInt(minSalary, 10) : minSalary;
  const max = typeof maxSalary === 'string' ? parseInt(maxSalary, 10) : maxSalary;

  if (isNaN(min) || isNaN(max)) {
    return null;
  }

  // Format as "min-max" in thousands if both are in thousands range
  // Otherwise format with full numbers
  if (min >= 1000 && max >= 1000) {
    const minK = Math.round(min / 1000);
    const maxK = Math.round(max / 1000);
    return `${minK}-${maxK}k`;
  }

  // Format with full numbers
  return `${min}-${max}`;
};
